import React, { useState } from 'react'
import { CardElement, useElements, useStripe } from '@stripe/react-stripe-js'
import { callAPI } from '../hooks/api'
import { useNavigate } from 'react-router'
import { createConfirmation } from 'react-confirm'
import CancelSubscription from '../components/CancelSubscription'
import { useAuth, parseProfileResponse } from '../hooks/getAuth'
import useAbility from '@/hooks/use-ability'

const StripeChangePlan = ({ email, newPlan, cardRequired }) => {
  const stripe = useStripe()
  const elements = useElements()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const navigate = useNavigate()
  const confirm = createConfirmation(CancelSubscription)
  const { setAuth, setIsLoggedIn } = useAuth()

  const { invalidateQuery } = useAbility()

  const handleSubmit = async (e, email) => {
    e.preventDefault()

    try {
      const proceed = await confirm({
        confirmation: `Once submitted, this action cannot be undone. Do you wish to continue?`,
      })

      if (proceed) {
        setLoading(true)
        // Create new payment method
        const { paymentMethod, error: pmError } =
          cardRequired ? await stripe.createPaymentMethod({
            type: 'card',
            card: elements.getElement('card'),
            billing_details: { email },
          }) : {paymentMethod: {}, error: false}

        if (pmError) {
          setError('Failed to create payment method. Please try again.')
          setLoading(false)
          return
        }

        // Call backend to create subscription
        const res = await callAPI('/change_plan', {
          session_key: JSON.parse(localStorage.getItem('auth')).sessionKey,
          tier: newPlan,
          payment_method_id: cardRequired ? paymentMethod.id : '', // Pass the new payment method ID
        })
        const { status, msg, clientSecret } = res

        const handleAuth = (userCredential) => {
          localStorage.setItem('auth', JSON.stringify(userCredential || {}))
          setAuth(userCredential)
          setIsLoggedIn(!!userCredential)
          invalidateQuery()
          navigate('/', { replace: true }) // Redirect to home
        }

        switch (status) {
          case 'fail':
            setError(msg)
            break

          case 'payment': {
            try {
              const { error, paymentIntent } = await stripe.confirmCardPayment(
                clientSecret,
                {
                  payment_method: paymentMethod.id,
                }
              )
              if (error) {
                setError('Payment failed. Please try again.')
              } else if (paymentIntent?.status === 'succeeded') {
                res.status = 'success'
                const userCredential = parseProfileResponse(res)
                handleAuth(userCredential)
              } else {
                handleAuth(null) // Clear auth on payment failure
              }
            } catch (err) {
              setError('An unexpected error occurred. Please try again.')
            }
            break
          }

          default: {
            const userCredential = parseProfileResponse(res)
            handleAuth(userCredential)
            break
          }
        }
      }
    } catch (error) {
      setError('An unexpected error occurred. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <>
      {cardRequired && <CardElement className="mt-2 mb-2" />}
      {cardRequired && error && <div style={{ color: 'red' }}>{error}</div>}
      <button
        onClick={(e) => handleSubmit(e, email)}
        disabled={!stripe || !elements || loading}
        className="bg-primary text-white px-7 mt-2 mb-4 py-3 text-sm font-medium uppercase rounded"
      >
        {loading ? 'Processing...' : (cardRequired ? 'Pay Now' : 'Change')}
      </button>
    </>
  )
}

export default StripeChangePlan
